import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  Package,
  DollarSign,
  Printer,
  Send,
  CheckCircle2,
  ArrowRight,
  Smartphone,
  Zap,
} from 'lucide-react';
import { Button } from './ui/button';

/**
 * SellHowItWorks
 * Progressive 3-step widget that reveals each stage as the user scrolls through it.
 * Mobile: stacked vertical flow with a left-side numbered timeline.
 * Desktop: 3-column card layout with animated progress bar (1, 2, 3) on top.
 */
const SellHowItWorks = () => {
  const [activeStep, setActiveStep] = useState(0); // 0,1,2, controls progress bar fill
  const stepRefs = [useRef(null), useRef(null), useRef(null)];

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = Number(entry.target.getAttribute('data-step-idx'));
            setActiveStep((prev) => Math.max(prev, idx));
          }
        });
      },
      { rootMargin: '-20% 0px -40% 0px', threshold: 0.3 }
    );
    stepRefs.forEach((r) => r.current && obs.observe(r.current));
    return () => obs.disconnect();
  }, []);

  const progressPct = activeStep === 0 ? 0 : activeStep === 1 ? 50 : 100;

  return (
    <section
      className="relative py-20 sm:py-24 lg:py-28 overflow-hidden bg-gradient-to-b from-white via-slate-50 to-white"
      data-testid="sell-how-it-works"
    >
      {/* subtle grid bg */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(to right, #0f172a 1px, transparent 1px), linear-gradient(to bottom, #0f172a 1px, transparent 1px)',
          backgroundSize: '64px 64px',
        }}
      />
      {/* ambient glows */}
      <div aria-hidden className="pointer-events-none absolute -top-24 -left-24 h-96 w-96 rounded-full bg-red-500/10 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-emerald-400/10 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* ===== HEADER ===== */}
        <div className="text-center mb-12 sm:mb-16">
          <span className="inline-block text-red-500 text-xs sm:text-sm font-bold uppercase tracking-[0.25em] mb-3">
            How It Works
          </span>
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
            From drawer to <span className="text-red-500">dollars</span>
            <br className="hidden sm:block" />
            <span className="sm:hidden"> </span>in 3 simple steps
          </h2>
          <p className="mt-5 text-sm sm:text-base text-slate-500 max-w-xl mx-auto">
            Turn your old devices into cash. No meetups, no haggling, no waiting.
          </p>
        </div>

        {/* ===== PROGRESS BAR (DESKTOP) ===== */}
        <div className="hidden md:block mb-12">
          <div className="relative max-w-4xl mx-auto">
            {/* Track */}
            <div className="absolute top-6 left-0 right-0 h-[2px] bg-slate-200" />
            {/* Filled */}
            <div
              className="absolute top-6 left-0 h-[2px] bg-gradient-to-r from-red-500 to-emerald-400 transition-all duration-700 ease-out"
              style={{ width: `${progressPct}%` }}
            />
            {/* Number markers */}
            <div className="relative flex justify-between">
              {[1, 2, 3].map((n, i) => {
                const reached = i <= activeStep;
                return (
                  <div key={n} className="flex flex-col items-center">
                    <div
                      className={`flex items-center justify-center w-12 h-12 rounded-full text-lg font-bold transition-all duration-500 ${
                        reached
                          ? 'bg-red-600 text-white shadow-lg shadow-red-600/40 scale-110'
                          : 'bg-white text-slate-400 border border-slate-300'
                      }`}
                      data-testid={`sell-step-${n}-marker`}
                    >
                      {n}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ===== STEPS ===== */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {/* STEP 1, Instant Quote */}
          <div
            ref={stepRefs[0]}
            data-step-idx="0"
            className={`group relative rounded-3xl border border-slate-200 bg-white shadow-lg shadow-slate-200/60 p-6 sm:p-7 transition-all duration-500 hover:border-red-300 hover:shadow-xl hover:-translate-y-1 ${
              activeStep >= 0 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
            data-testid="sell-step-1"
          >
            {/* Mobile number pill */}
            <div className="md:hidden inline-flex items-center gap-2 bg-red-600/20 border border-red-500/30 rounded-full px-3 py-1 text-red-300 text-xs font-bold mb-4">
              <span className="w-5 h-5 rounded-full bg-red-600 text-white flex items-center justify-center text-[10px]">1</span>
              STEP ONE
            </div>

            {/* Visual mockup, quote card */}
            <div className="relative bg-slate-800 rounded-2xl p-5 mb-6 border border-slate-700/50 overflow-hidden">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center text-white font-bold">
                  J
                </div>
                <div className="flex-1">
                  <p className="text-white text-sm font-semibold">Jenny wants to sell her phone</p>
                  <div className="flex items-center gap-1 text-xs text-emerald-400 mt-0.5">
                    <Search className="w-3 h-3" />
                    <span>Searching models…</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 bg-slate-900 rounded-xl p-3 border border-slate-700/40">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Device</p>
                  <p className="text-sm font-bold text-white">Phone 13</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Condition</p>
                  <p className="text-sm font-bold text-white">Good</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Offer</p>
                  <p className="text-sm font-bold text-emerald-400">$540</p>
                </div>
              </div>
              <div className="mt-3 bg-red-600 text-white text-center text-xs font-bold py-2.5 rounded-lg shadow shadow-red-600/30">
                Accept Offer
              </div>
              {/* Floating icon */}
              <div className="absolute -top-2 -right-2 w-9 h-9 rounded-full bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center shadow-lg shadow-red-500/30 animate-pulse">
                <Search className="w-4 h-4 text-white" />
              </div>
            </div>

            <h3 className="text-xl font-bold text-slate-900 mb-2">Get your Instant Quote</h3>
            <p className="text-sm text-slate-500 leading-relaxed">
              Pick your device and condition. We&apos;ll show your offer before you say{' '}
              <span className="text-slate-700 italic">&ldquo;goodbye, clutter.&rdquo;</span>
            </p>
          </div>

          {/* STEP 2, Ship for Free */}
          <div
            ref={stepRefs[1]}
            data-step-idx="1"
            className={`group relative rounded-3xl border border-slate-200 bg-white shadow-lg shadow-slate-200/60 p-6 sm:p-7 transition-all duration-500 hover:border-amber-300 hover:shadow-xl hover:-translate-y-1 ${
              activeStep >= 1 ? 'opacity-100 translate-y-0' : 'opacity-40 translate-y-4'
            }`}
            data-testid="sell-step-2"
          >
            <div className="md:hidden inline-flex items-center gap-2 bg-amber-600/20 border border-amber-500/30 rounded-full px-3 py-1 text-amber-300 text-xs font-bold mb-4">
              <span className="w-5 h-5 rounded-full bg-amber-600 text-white flex items-center justify-center text-[10px]">2</span>
              STEP TWO
            </div>

            {/* Visual mockup, Print → Send → Done */}
            <div className="relative bg-slate-800 rounded-2xl p-5 mb-6 border border-slate-700/50 overflow-hidden">
              <div className="flex items-center justify-center mb-5 mt-2">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/40 flex items-center justify-center">
                  <Package className="w-8 h-8 text-amber-300" />
                </div>
              </div>
              <div className="flex items-center justify-between text-xs font-semibold text-slate-300">
                <div className="flex flex-col items-center gap-1">
                  <Printer className="w-4 h-4 text-amber-400" />
                  <span>Print</span>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-600" />
                <div className="flex flex-col items-center gap-1">
                  <Send className="w-4 h-4 text-amber-400" />
                  <span>Send</span>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-600" />
                <div className="flex flex-col items-center gap-1">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span>Done</span>
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-slate-700/40">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-wider text-slate-500">Shipping</span>
                  <span className="text-xs font-bold text-emerald-400">FREE &amp; TRACKED</span>
                </div>
              </div>
            </div>

            <h3 className="text-xl font-bold text-slate-900 mb-2">Ship for Free</h3>
            <p className="text-sm text-slate-500 leading-relaxed">
              Print our prepaid label, drop it in any box, and send it off. We handle the rest.
            </p>
          </div>

          {/* STEP 3, Get Paid Fast */}
          <div
            ref={stepRefs[2]}
            data-step-idx="2"
            className={`group relative rounded-3xl border border-slate-200 bg-white shadow-lg shadow-slate-200/60 p-6 sm:p-7 transition-all duration-500 hover:border-emerald-300 hover:shadow-xl hover:-translate-y-1 ${
              activeStep >= 2 ? 'opacity-100 translate-y-0' : 'opacity-40 translate-y-4'
            }`}
            data-testid="sell-step-3"
          >
            <div className="md:hidden inline-flex items-center gap-2 bg-emerald-600/20 border border-emerald-500/30 rounded-full px-3 py-1 text-emerald-300 text-xs font-bold mb-4">
              <span className="w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center text-[10px]">3</span>
              STEP THREE
            </div>

            {/* Visual mockup, phone lock screen w/ payment */}
            <div className="relative bg-slate-800 rounded-2xl p-5 mb-6 border border-slate-700/50 overflow-hidden">
              <div className="mx-auto w-32 h-40 bg-gradient-to-b from-slate-900 to-slate-950 rounded-[20px] border-2 border-slate-700 shadow-xl relative overflow-hidden">
                {/* Notch */}
                <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-12 h-3 bg-black rounded-full" />
                {/* Status bar */}
                <div className="flex justify-between items-center text-[7px] text-white pt-1.5 px-3">
                  <span className="font-semibold">9:41</span>
                  <div className="flex gap-0.5">
                    <div className="w-0.5 h-1 bg-white rounded-sm" />
                    <div className="w-0.5 h-1.5 bg-white rounded-sm" />
                    <div className="w-0.5 h-2 bg-white rounded-sm" />
                  </div>
                </div>
                {/* Notification */}
                <div className="mx-2 mt-3 bg-white/10 backdrop-blur-sm rounded-lg p-1.5 border border-white/10">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-emerald-500 flex items-center justify-center">
                      <DollarSign className="w-2 h-2 text-white" />
                    </div>
                    <span className="text-[6px] text-white font-semibold">2A Electronics</span>
                  </div>
                  <p className="text-[7px] text-white mt-0.5 leading-tight">
                    Payment sent: <span className="font-bold text-emerald-400">$540.00</span>
                  </p>
                </div>
                {/* Time */}
                <p className="text-white text-center text-2xl font-bold mt-4">9:41</p>
                <p className="text-white/70 text-center text-[7px]">Monday, June 6</p>
              </div>
              {/* Floating cash icon */}
              <div className="absolute -top-2 -right-2 w-9 h-9 rounded-full bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center shadow-lg shadow-emerald-500/30 animate-bounce">
                <DollarSign className="w-4 h-4 text-white" />
              </div>
            </div>

            <h3 className="text-xl font-bold text-slate-900 mb-2">Get Paid Fast</h3>
            <p className="text-sm text-slate-500 leading-relaxed">
              No listings, no waiting, no meeting strangers. Get paid the same day we inspect your device.{' '}
              <span className="text-emerald-400 font-semibold">Cha-ching! 💸</span>
            </p>
          </div>
        </div>

        {/* ===== CTA ===== */}
        <div className="mt-12 sm:mt-16 text-center">
          <Link to="/sell" data-testid="sell-how-it-works-cta">
            <Button
              size="lg"
              className="bg-red-600 hover:bg-red-700 text-white text-base font-bold px-8 py-6 rounded-full shadow-xl shadow-red-600/30 transition-all hover:-translate-y-0.5 hover:shadow-red-600/50"
            >
              <Zap className="w-5 h-5 mr-2" />
              Get Your Instant Quote
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
          <p className="mt-4 text-xs sm:text-sm text-slate-500">
            Free shipping · Same-day payment · Zero listing fees
          </p>
        </div>
      </div>
    </section>
  );
};

export default SellHowItWorks;
